import { useLingui } from "@lingui/react/macro"
import type { ConversationMessageSenderKind } from "@/types/v2/conversation"

// Outbound messages get a circular avatar to the right of
// the bubble identifying who sent it. The three variants
// match the legacy chat:
// - whatsapp_business_app → green circle with WhatsApp logo
// - ai                    → cream circle with sparkle glyph
// - employee              → solid color circle with initials
//
// Inbound messages render the contact avatar separately in
// `MessageBubble`; this component only handles the outbound
// side.

const SIZE_CLASS = {
  sm: "size-3.5",
  // Compact variant used in the deal activity timeline,
  // matching the existing 24-px icon column.
  compact: "size-6",
  md: "size-7",
} as const

// Tuned per size — the WhatsApp logo and sparkle have
// different optical centers so we scale them slightly
// differently per ring size.
const WHATSAPP_PX = {
  sm: 8,
  compact: 14,
  md: 16,
} as const

const SPARKLE_PX = {
  sm: 7,
  compact: 11,
  md: 13,
} as const

const INITIALS_TEXT = {
  sm: "text-[7px]",
  compact: "text-[10px]",
  md: "text-[11px]",
} as const

type Size = keyof typeof SIZE_CLASS

interface OutboundSenderAvatarProps {
  senderKind: ConversationMessageSenderKind
  initials?: string | null
  size?: Size
  className?: string
}

export function OutboundSenderAvatar({
  senderKind,
  initials,
  size = "md",
  className = "",
}: OutboundSenderAvatarProps) {
  const { t } = useLingui()
  const sizeClass = SIZE_CLASS[size]
  const wrapperClass = `shrink-0 flex items-center justify-center rounded-full ${sizeClass} ${className}`

  // Distinguish the standalone bubble avatar from the small
  // chip rendered inside the label row, so tests can target
  // either independently.
  const testId =
    size === "sm" ? `${senderKind}-avatar-chip` : `${senderKind}-avatar`

  if (senderKind === "whatsapp_business_app") {
    return (
      <div
        className={`${wrapperClass} bg-[#25D366]`}
        data-testid={testId}
        aria-label={t`WhatsApp Business App`}
      >
        <WhatsAppLogoSvg px={WHATSAPP_PX[size]} fill="#FFFFFF" />
      </div>
    )
  }

  if (senderKind === "ai") {
    return (
      <div
        className={`${wrapperClass} bg-[#FAF1E6] border border-solid border-[#EBD9C2]`}
        data-testid={testId}
        aria-label={t`AI`}
      >
        <SparkleSvg px={SPARKLE_PX[size]} fill="#B86A2D" />
      </div>
    )
  }

  if (senderKind === "employee") {
    return (
      <div
        className={`${wrapperClass} bg-[#6F58B5]`}
        data-testid={testId}
      >
        <span
          className={`font-semibold text-white ${INITIALS_TEXT[size]} leading-3.5`}
        >
          {(initials ?? "").trim() || "·"}
        </span>
      </div>
    )
  }

  return null
}

// The label rendered above outbound bubbles, mirroring the
// Paper design (e.g. "Sent from WhatsApp Business app",
// "AI replied for you"). Pass the full text string in.
// Employee sends intentionally do not render this label —
// `MessageBubble` returns `null` from `outboundLabelText`
// for the employee branch (CON-106).
interface OutboundSenderLabelProps {
  senderKind: ConversationMessageSenderKind
  text: string
}

export function OutboundSenderLabel({
  senderKind,
  text,
}: OutboundSenderLabelProps) {
  if (senderKind === "whatsapp_business_app") {
    return (
      <div className="flex items-center pr-1 gap-1.5">
        <WhatsAppLogoSvg px={12} fill="#0F8A4A" />
        <span className="tracking-[0.04em] font-semibold text-[#0F8A4A] text-[11px] leading-3.5">
          {text}
        </span>
      </div>
    )
  }

  if (senderKind === "ai") {
    return (
      <div className="flex items-center pr-1 gap-1.5">
        <SparkleSvg px={11} fill="#B86A2D" />
        <span className="tracking-[0.04em] font-medium text-[#B86A2D] text-[11px] leading-3.5">
          {text}
        </span>
      </div>
    )
  }

  return null
}

// Official WhatsApp speech bubble — same paths as the legacy
// `Svgs::WhatsappSvg` (viewBox 0 0 32 32). Color is set by
// the parent via `fill`.
function WhatsAppLogoSvg({ px, fill }: { px: number; fill: string }) {
  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      style={{ flexShrink: 0 }}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M27.012 4.876C24.084 1.94 20.189.323 16.04.321 7.486.321.523 7.281.52 15.838c0 2.735.715 5.405 2.072 7.76L.389 31.679l8.27-2.169a15.55 15.55 0 0 0 7.374 1.878h.007c8.554 0 15.518-6.961 15.521-15.519.001-4.148-1.612-8.046-4.549-10.992m-10.972 23.88h-.005a12.9 12.9 0 0 1-6.572-1.799l-.471-.279-4.907 1.287 1.31-4.784-.307-.49a12.9 12.9 0 0 1-1.977-6.881c.003-7.114 5.792-12.901 12.937-12.901a12.85 12.85 0 0 1 9.139 3.79 12.85 12.85 0 0 1 3.781 9.146c-.003 7.115-5.792 12.91-12.928 12.91m7.089-9.667c-.388-.194-2.297-1.134-2.654-1.264-.355-.13-.614-.194-.873.195-.259.388-1 1.263-1.227 1.522-.226.259-.452.291-.84.097-.389-.194-1.64-.604-3.123-1.927-1.154-1.03-1.933-2.301-2.16-2.689-.226-.388-.024-.598.17-.792.175-.174.388-.453.582-.68.194-.226.259-.388.388-.647.13-.259.065-.485-.032-.679-.097-.194-.872-2.105-1.196-2.882-.315-.756-.635-.653-.872-.665a16 16 0 0 0-.744-.014.43.43 0 0 0-.323.146c-.111.121-.42.41-.706.795-.43.583-1.745 1.945-1.745 3.857 0 1.913 1.395 3.762 1.589 4.022.194.259 2.744 4.191 6.65 5.879q1.394.601 2.178.886c.915.292 1.747.25 2.405.151.733-.109 2.258-.923 2.575-1.815.318-.892.318-1.656.222-1.815-.097-.16-.355-.255-.744-.45"
        fill={fill}
      />
    </svg>
  )
}

// Four-point sparkle/asterisk used as the AI marker —
// mirrors the Paper design's accent glyph.
function SparkleSvg({ px, fill }: { px: number; fill: string }) {
  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      style={{ flexShrink: 0 }}
    >
      <path
        d="M8 1C8 4 9 7 12 7C15 7 8 8 8 8C8 8 15 9 12 9C9 9 8 12 8 15C8 12 7 9 4 9C1 9 8 8 8 8C8 8 1 7 4 7C7 7 8 4 8 1Z"
        fill={fill}
      />
    </svg>
  )
}
